<template>
  <div class="vue-screenshots">
    <Carousel
      id="gallery"
      v-bind="galleryConfig"
      v-model="currentSlide"
      class="carousel__main"
    >
      <Slide
        v-for="image in list"
        :key="image.id"
      >
        <img
          :src="image.file"
          alt="Gallery Image"
          class="gallery-image"
          @click="openFullscreen"
        />
      </Slide>
      <template #addons>
        <Navigation />
      </template>
    </Carousel>

    <Carousel
      id="thumbnails"
      ref="carouselRef"
      v-bind="thumbnailsConfig"
      v-model="currentSlide"
      :class="{ isFreeze: isAllImagesVisible }"
      @slideEnd="slideEnd"
    >
      <Slide
        v-for="image in list"
        :key="image.id"
        class="slideEl"
      >
        <template #default="{ currentIndex, isActive }">
          <div
            :class="['thumbnail', { 'is-active': isActive }]"
            @click="slideTo(currentIndex)"
          >
            <img
              :src="image.file"
              class="thumbnail-image"
              @load="handleImageLoad"
            />
          </div>
        </template>
      </Slide>
    </Carousel>

    <CarouselPagination
      ref="paginationRef"
      :carouselRef="carouselRef"
    />

    <div
      v-show="isFullScreen"
      class="fullscreen"
      @click="closeFullscreen"
    >
      <div
        class="fullscreen__arrow fullscreen__prev"
        :class="{ disabled: isFirstImage }"
        @click="prevSlide"
      >
        <ArrowLeft />
      </div>
      <img
        ref="fullscreenImageRef"
        :src="fullscreenImage"
        class="fullscreen__image"
        @click.prevent
      />
      <div
        class="fullscreen__arrow fullscreen__next"
        :class="{ disabled: isLastImage }"
        @click="nextSlide"
      >
        <ArrowLeft />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, onUnmounted, computed, watch } from 'vue';
import 'vue3-carousel/dist/carousel.css';
import { Carousel, Slide, Navigation } from 'vue3-carousel';
import CarouselPagination from '@/components/Carousel/CarouselPagination.vue';
import { Screenshots } from '@/types/manuals';
import ArrowLeft from '@/components/icons/CarouselLeft.vue';

const props = defineProps<{
  list: Screenshots[];
}>();

const gap = 4;
const currentSlide = ref(0);
const fullscreenImageRef = ref();
const carouselRef = ref();
const paginationRef = ref();
const deviceWidth = ref();
const isFullScreen = ref(false);
const isAllImagesVisible = ref(false);
const isFirstImage = computed(() => carouselRef.value?.minSlide === currentSlide.value);
const isLastImage = computed(() => carouselRef.value?.maxSlide === currentSlide.value);
const fullscreenImage = computed(() => props.list[currentSlide.value].file);
const loadedImagesCount = ref(0);
const allImagesLoaded = computed(() => loadedImagesCount.value === props.list.length);

const handleImageLoad = () => {
  loadedImagesCount.value++;
};

const galleryConfig = {
  wrapAround: false,
  itemsToShow: 1,
  height: 452,
  mouseDrag: false,
  touchDrag: false,
  slideEffect: 'fade',
};
const thumbnailsConfig = {
  wrapAround: false,
  gap,
  itemsToShow: 'auto',
  snapAlign: 'start',
  touchDrag: true,
  mouseDrag: true,
  height: 'auto',
  dynamicSlides: true,
};

const openFullscreen = () => {
  isFullScreen.value = true;
  document.body.style.overflow = 'hidden';
};

const closeFullscreen = () => {
  isFullScreen.value = false;
  document.body.style.overflow = '';
};

const nextSlide = (event?: Event) => {
  event?.stopPropagation();
  if (isLastImage.value) {
    return false;
  }
  carouselRef.value.next();
};
const prevSlide = (event?: Event) => {
  event?.stopPropagation();
  if (isFirstImage.value) {
    return false;
  }
  carouselRef.value.prev();
};

const slideTo = (nextSlide: number) => {
  currentSlide.value = nextSlide;
  if (window.innerWidth < 577) {
    openFullscreen();
  }
};

const checkAllThumbnailsVisible = () => {
  if (!carouselRef.value || !props.list?.length) { return false; }
  const { slides } = carouselRef.value;
  let totalWidth = 0;
  slides.forEach((slide: Record<string, any>, i: number) => {
    const slideWidth = slides[i]?.vnode?.el?.offsetWidth || 0;
    totalWidth += slideWidth + gap;
  });
  isAllImagesVisible.value = totalWidth < carouselRef.value.viewport.offsetWidth;
  return isAllImagesVisible.value;
};

const resetThumbnails = () => {
  if (!carouselRef.value) { return; }
  carouselRef.value.slideTo(0, { transition: 0 });
};

const resizeAction = () => {
  carouselRef.value.updateSlideSize();
  resetThumbnails();
  deviceWidth.value = window.innerWidth;
  checkAllThumbnailsVisible();
};

const slideEnd = () => {
  paginationRef.value?.updVisibleCards();
};

let startX = 0;
const touchstartFullscreen = (e: any) => {
  startX = e.touches[0].clientX;
};
const touchmoveFullscreen = (e: any) => {
  const currentX = e.touches[0].clientX;
  const diffX = currentX - startX;
  const minMove = 10;

  if (Math.abs(diffX) > minMove) {
    if (diffX > 0) {
      prevSlide();
    } else {
      nextSlide();
    }
  }
};

watch(() => allImagesLoaded.value, resizeAction);

onMounted(() => {
  fullscreenImageRef.value.addEventListener('touchstart', touchstartFullscreen);
  fullscreenImageRef.value.addEventListener('touchmove', touchmoveFullscreen);
  deviceWidth.value = window.innerWidth;
  window.addEventListener('resize', resizeAction);
  setTimeout(() => {
    resizeAction();
  }, 1000);
});

onUnmounted(() => {
  window.removeEventListener('resize', resizeAction);
  fullscreenImageRef.value?.removeEventListener('touchstart', touchstartFullscreen);
  fullscreenImageRef.value?.removeEventListener('touchmove', touchmoveFullscreen);
});

</script>

<style lang="scss">
@import "@/assets/style/include";

.vue-screenshots {
  .carousel {
    user-select: none;

    .carousel__next {
      inset-inline-end: -5%;
    }
    .carousel__prev {
      inset-inline-start: -5%;
    }

    img {
      border-radius: 4px;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .gallery-image {
      border-radius: 8px;
      border: 2px solid $colorBlue1;
      object-fit: scale-down;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      cursor: url("@/assets/svg/zoom_in.svg") 0 0, auto;
      transition: 0.2s filter;

      &:hover {
        filter: brightness(70%);
      }
    }

    $maxWidth: 102px;

    .thumbnail-image {
      max-width: $maxWidth;
      width: auto;
      max-height: 60px;
    }

    &#thumbnails {
      margin: 10px 0;

      &.isFreeze {
        .carousel__track {
          transform: translateX(0) !important;
        }
      }

      .carousel__slide {
        max-width: $maxWidth
      }
    }

    .thumbnail {
      height: 100%;
      width: 100%;
      max-width: $maxWidth;
      cursor: pointer;
      opacity: 1;
      display: inline-flex;
      border: 2px solid transparent;
      border-radius: 8px;
      position: relative;
      transition: .2s;

      &:hover {
        border: 2px solid $colorBlue1;
      }

      &.is-active {
        border: 2px solid $colorBlue1;
        overflow: hidden;

        &:after {
          content: '';
          display: block;
          width: 100%;
          height: 100%;
          background: $colorBlue1;
          opacity: 0.3;
          top: 0;
          position: absolute;
        }
      }
    }

    .carousel__pagination {
      top: 100%;
      margin-top: 10px;
    }
  }

  .fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, .8);
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 1000;
    cursor: zoom-out;

    &__arrow {
      color: $colorBlue1;
      width: 50px;
      height: 200px;
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      margin: 0 30px;
      transition: .2s;

      svg {
        width: 32px;
        height: 54px;
      }

      &:hover {
        svg {
          transform: scale(1.1);
        }
      }
      &:active {
        color: $colorBlueHover;
      }

      &.disabled {
        color: $bgLight5;
        cursor: auto;

        &:hover {
          svg {
            transform: scale(1);
          }
        }
        &:active {
          color: $bgLight5;
        }
      }
    }

    &__next {
      transform: rotate(180deg);
    }

    &__image {
      user-select: none;
      min-width: 200px;
      max-width: 80%;
      max-height: 90%;
      object-fit: contain;
      border: 2px solid $colorBlue1;
      border-radius: 8px;
    }
  }
}


@include media-breakpoint-down("tablet") {

  .vue-screenshots {
    .carousel {

      $maxWidth: 164px;

      .thumbnail-image {
        max-width: $maxWidth;
        max-height: 92px;
      }

      &#thumbnails {
        margin-bottom: 0;

        .carousel__slide {
          max-width: 100%;
        }
      }

      .thumbnail {
        max-width: $maxWidth;
      }
    }

    .fullscreen {
      cursor: auto;
      justify-content: center;

      &__arrow {
        display: none;
      }
      &__image {
        max-width: 100%;
        border-radius: 0;
        border: 0;
      }
    }

    .pagination,
    .carousel__next,
    .carousel__prev {
      display: none;
    }
  }
}

@include media-breakpoint-down("mobile") {

  .vue-screenshots {

    .carousel {

      &__main {
        display: none;
      }

      $maxWidth: 210px;
      .thumbnail-image {
        max-width: $maxWidth;
        max-height: 156px;
      }
      &#thumbnails {
        .carousel__slide {
          max-width: 100%;
        }
      }
      .thumbnail {
        max-width: $maxWidth;
      }
    }

  }
}

</style>
